"use client"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  FlexColumn,
  Separator,
  useToasts,
} from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useCallback, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import {
  MAX_PRICE_CHANGE_PERCENTAGE,
  PriceChangeError,
} from "../../../components/PriceChange"
import { usePriceValidation } from "../../../components/PriceChange/usePriceValidation"
import { buildPricingLookup } from "../../../components/QuoteReceipt"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks/useAddress"
import type { SchedulableResponse } from "../../../polychain/hooks/useScheduler"
import type { TransactionIdentifier } from "../../../stores/currentTransactionStore"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { AcceptOfferFlowState } from "../../flow-state"
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import {
  useSetItemImageMap,
  useSetItemImageMapFragment,
} from "../../hooks/useSetItemImageMap"
import {
  useSetQuoteItems,
  useSetQuoteItemsFragment,
} from "../../hooks/useSetQuoteItems"
import {
  useSetTotalUsdPrice,
  useSetTotalUsdPriceFragment,
} from "../../hooks/useSetTotalUsdPrice"
import { AcceptOfferToast } from "../../ToastHandler/AcceptOfferToast/AcceptOfferToast"
import { CREATOR_FEE_ENFORCED_LOCAL_STORAGE } from "../constants"
import { AcceptOfferFlow_quoteFragment } from "./AcceptOfferFlow.fragment"
import { AcceptOfferFlowSkeleton } from "./AcceptOfferFlowSkeleton"
import { AcceptOfferReceipt } from "./components/AcceptOfferReceipt"
import { AcceptOfferFlowStatus } from "./components/AcceptOfferStatus"
import { AcceptOffersTimeline } from "./components/AcceptOfferTimeline/AcceptOfferTimeline"

type Props = Pick<AcceptOfferFlowState, "items">

const query = graphql(
  `
    query AcceptOfferFlowQuery(
      $identifiers: [ItemQuantityInput!]!
      $address: Address
    ) {
      acceptOffersQuote(itemQuantities: $identifiers, address: $address) {
        ...AcceptOfferFlow_quote
        itemQuotes {
          ...useSetQuoteItems
          ...useSetItemImageMap
          ...useSetTotalUsdPrice
        }
      }
    }
  `,
  [
    AcceptOfferFlow_quoteFragment,
    useSetQuoteItemsFragment,
    useSetItemImageMapFragment,
    useSetTotalUsdPriceFragment,
  ],
)

export function AcceptOfferFlow({ items: itemsWithOrder }: Props) {
  const t = useTranslations("wallet.AcceptOfferFlow")
  const address = useAddress()
  const { addCustomToast } = useToasts()
  const [storedCreatorFeePreference, setStoredCreatorFeePreference] =
    useLocalStorage(CREATOR_FEE_ENFORCED_LOCAL_STORAGE, true)
  const [applyOptionalCreatorFees, setApplyOptionalCreatorFeesState] = useState(
    storedCreatorFeePreference,
  )

  const { flow, itemQuotes } = useSnapshot(FlowState)
  const mode = (flow as AcceptOfferFlowState)?.mode

  const setApplyOptionalCreatorFees = (value: boolean) => {
    setApplyOptionalCreatorFeesState(value)
    setStoredCreatorFeePreference(value)
  }
  const initialPrices = useMemo(
    () => buildPricingLookup(itemsWithOrder),
    [itemsWithOrder],
  )
  const [{ data, fetching }] = useQuery({
    query,
    variables: {
      identifiers: itemsWithOrder.map(item => ({
        item: {
          tokenId: item.tokenId,
          contractAddress: item.contractAddress,
          chain: item.chain.identifier,
        },
        quantity: item.quantity,
        orderId: item.orderId,
      })),
      address,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  useSetQuoteItems(data?.acceptOffersQuote.itemQuotes)
  useSetItemImageMap(data?.acceptOffersQuote.itemQuotes)
  useSetTotalUsdPrice(data?.acceptOffersQuote.itemQuotes)
  const priceValidation = usePriceValidation({
    itemsWithPricing: itemsWithOrder,
    flow: "accept-offer",
    itemQuotes,
  })

  useEndFlowOnAccountChange()

  const [allActionsCompleted] = useAllActionsCompleted()

  const handleAllActionsCompleted = useCallback(
    (
      _response: SchedulableResponse,
      receiptTransactions: TransactionIdentifier[],
    ) => {
      // Only show toast if mode is minimized
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const { itemQuotes, itemImageMap, flow } = FlowState

        addCustomToast(
          () => (
            <AcceptOfferToast
              flow={flow}
              itemImageMap={itemImageMap}
              itemQuotes={itemQuotes}
              transactionIdentifiers={receiptTransactions}
            />
          ),
          {
            id: "accept-offer-toast",
            duration: Number.POSITIVE_INFINITY,
          },
        )

        // End the flow after toast is shown
        FlowActions.endFlow()
      }
    },
    [mode, addCustomToast],
  )

  if (fetching) {
    return <AcceptOfferFlowSkeleton count={itemsWithOrder.length} />
  }

  if (!data?.acceptOffersQuote) {
    throw new Error("There was a problem with accepting offer.")
  }

  const quote = readFragment(
    AcceptOfferFlow_quoteFragment,
    data.acceptOffersQuote,
  )
  const items = quote.itemQuotes.map(itemQuote => itemQuote.item)

  // Item not found
  if (!items.every(item => item.__typename !== "ItemIdentifierResult")) {
    return null
  }

  if (
    priceValidation.changedItems.some(
      item =>
        Math.abs(item.priceDifference) >
        (item.initialPrice * MAX_PRICE_CHANGE_PERCENTAGE) / 100,
    )
  ) {
    return <PriceChangeError priceValidation={priceValidation} />
  }

  return (
    <>
      {allActionsCompleted ? (
        <AcceptOfferFlowStatus />
      ) : (
        <>
          <BottomSheetHeader>
            <BottomSheetHeaderTitle>
              {t("accept", { count: items.length })}
            </BottomSheetHeaderTitle>
          </BottomSheetHeader>

          <BottomSheetBody className="mt-2 pb-2">
            <FlexColumn className="size-full">
              <AcceptOfferReceipt
                applyCreatorFee={applyOptionalCreatorFees}
                initialPrices={initialPrices}
                onCreatorFeeChange={setApplyOptionalCreatorFees}
                quote={quote}
              />

              <Separator />

              <Flex className="w-full py-4">
                <AcceptOffersTimeline
                  applyOptionalCreatorFees={applyOptionalCreatorFees}
                  onAllActionsCompleted={handleAllActionsCompleted}
                  priceValidation={priceValidation}
                  quote={quote}
                />
              </Flex>
            </FlexColumn>
          </BottomSheetBody>
        </>
      )}
    </>
  )
}
